import type * as React from "react";
import { type CSSProperties, Fragment, memo, useEffect, useRef } from "react";
import classNames from "classnames";
import { copyLastPackageEntry, loadACartEntry, loadPnACartEntry } from "../../../../redux/actions/cartPageActions";
import { useDispatch, useSelector } from "react-redux";
import { IconType } from "../../../../../../../common/components/icon/IconType";
import { type CartEntry, type LineItem, type ResolvedGroupedCartEntry } from "../../../../models/CartData";

import PackageEntryList from "../PackageEntryList";
import Spinner from "../../../../../../../common/components/Spinner/Spinner";
import { isConfiguratorProduct, type ProductData } from "../../../../../../../models/ProductData";
import {
  selectCartEntryHasSurcharge,
  selectSubtotal,
  selectTotalQty,
  selectTotalSurcharge,
} from "../../../../redux/selectors/productEntrySelectors";
import { type RootState } from "../../../../../../../redux/store";
import { Link, LinkButton, LinkType } from "../../../../../../../common/components/Link";
import { Icon } from "../../../../../../../common/components/icon/Icon";
import CartEntryProductInfo from "../../CartEntryProductInfo";
import { ManualPackageRestrictionContext } from "../../../../../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import {
  selectIsCartAboveThreshold,
  selectLineItem,
  selectPackageEntry,
} from "../../../../redux/selectors/cartSelectors";
import uniq from "lodash/uniq";
import { DeliverySubtotal, PriceSubtotal, QuantitySubtotal } from "~/feature/cart/cart/components/commonParts";
import { type PriceData } from "../../../../../../../models/PriceData";
import { IncludedSurcharge } from "../../../../../../../common/components/Product/ProductParams";
import { useTranslation } from "react-i18next";
import { getPhysicalDeliveries } from "~/utils/cartUpdating/delivery/stockDataUtils";
import { calculateSalesCampaign4PDP } from "~/SalesCampaign/utils";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { SalesDataProvider } from "~/SalesCampaign/useSalesData";
import { CART_QTY_EXCEEDS_SALES_ERROR_KEY } from "~/SalesCampaign/constants";
import { useCartPageContext } from "~/feature/cart/cart/CartPageContext";
import { useIsBundledQuote } from "~/feature/cart/cart/IsBundledQuoteContext";
import { isProductEntryFcc } from "~/feature/cart/cart/components/CartPage/ProductTable/ProductEntryList/productEntryHelper";
import { Alert } from "~/common/m3/Alert/Alert";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { NO_PRICE_REFERENCE } from "~/utils/constants";

export interface ProductEntryProps {
  groupingEntry: ResolvedGroupedCartEntry;
  className?: string;
  editMode?: boolean;
  index: number;
  style?: CSSProperties;
  onRender?: (element: Element) => void;
  onDelete: (productCode: string, designation: string) => void;
}

const PlainProductEntry = (props: ProductEntryProps) => {
  const { groupingEntry: resolvedEntry, className, editMode, style, onRender, index, onDelete } = props;
  const { t } = useTranslation();

  const { showSubtotal } = useCartPageContext();

  const isInCheckout = !editMode;
  const isAboveThreshold = useSelector(selectIsCartAboveThreshold);
  const entry = resolvedEntry.cartEntry;
  const productDescriptors = resolvedEntry.descriptors;
  const product = productDescriptors[0].base;

  const allPackEntries = resolvedEntry.packGroups.flatMap(($) => $.packEntries);

  const isPnaLoading = productDescriptors.some((el) => el.isLoading);
  const isPnaError = productDescriptors.some((el) => el.isError);
  let isPnaLoaded: boolean;
  if (isInCheckout) {
    isPnaLoaded = productDescriptors.every((el) => el.availability);
  } else {
    isPnaLoaded = productDescriptors.every((el) => el.isLoaded);
  }

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onRender && rootRef.current) {
      window.setTimeout(() => {
        if (onRender && rootRef.current) {
          onRender(rootRef.current);
        }
      }, 16);
    }
  }, [onRender, entry, ...productDescriptors]);

  const cls = classNames(className, "scha__cart__product-entry productentry_" + props.index);

  const isEntireEntryFromQuote = allPackEntries.every(($) => !!$.quoteNumber);
  const canOnlyLoadAvailability = useIsBundledQuote() || isEntireEntryFromQuote;

  const showPleaseWait = isPnaLoading && editMode && isAboveThreshold;

  let showLoadPnaLink = false;
  if (editMode) {
    if (canOnlyLoadAvailability) {
      showLoadPnaLink = !productDescriptors.every((el) => el.availability);
    } else {
      showLoadPnaLink = !productDescriptors.every((el) => el.isLoaded);
    }
  }

  const packCodes = getUniquePackageCodes(allPackEntries);
  const isAllRestricted = allPackEntries.every((pack) => pack.restrictedPackage);
  const lastGroupIndex = resolvedEntry.packGroups.length - 1;
  const disableWS = window.SCHA.config.disableAllWS;

  const saleData = calculateSalesCampaign4PDP({ productDescriptor: productDescriptors[0] });
  const baseDescriptor = productDescriptors.find(
    (productDescriptor) => productDescriptor.priceReference === NO_PRICE_REFERENCE,
  );
  const isOnSale = useSalesHelper(baseDescriptor).isProductOnSale();

  const isFcc = isProductEntryFcc(props.groupingEntry);

  return (
    <SalesDataProvider value={saleData.data}>
      <div className={cls} style={style} data-test-id={`product-entry`} ref={rootRef} data-index={index}>
        <div className="scha__cart__product-table__left-side">
          {isOnSale && <SaleTag className={"scha__cart__sale-tag"} size={40} />}
          <ManualPackageRestrictionContext
            restrictedPackNo={isAllRestricted && packCodes.length === 1 ? packCodes[0] : ""}
          >
            <CartEntryProductInfo productDescriptor={productDescriptors[0]} isFcc={isFcc} />
          </ManualPackageRestrictionContext>
        </div>

        <div className="scha__cart__product-table__right-side">
          {showPleaseWait && <Spinner className={"my-2"} />}

          {resolvedEntry.packGroups.map((group, groupNo) => (
            <Fragment key={group.packEntryIds.join("-")}>
              <PackageEntryList
                editMode={editMode}
                packEntryIds={group.packEntryIds}
                packageIndex={index}
                onDelete={() => {
                  onDelete(product.code, product.designation);
                }}
              />
              {!isConfiguratorProduct(product) && (
                <div className="mt-2 mb-3">
                  {(editMode || !isPnaLoaded) && (
                    <div className={"scha__cart__product-table__right-side__summary-container"}>
                      {!disableWS && editMode && product.quantityVisible && (
                        <div className={"pl-3"}>
                          <CopyLastRowLink entryId={group.packEntryIds[group.packEntryIds.length - 1]} />
                        </div>
                      )}
                      {showLoadPnaLink && groupNo === lastGroupIndex && (
                        <div className={"pl-2"}>
                          <LoadPnaLink
                            productCode={product.code}
                            disabled={disableWS || isPnaLoading}
                            isEditMode={editMode}
                            loadAvailabilityOnly={canOnlyLoadAvailability}
                          />
                        </div>
                      )}

                      {!showLoadPnaLink && groupNo === lastGroupIndex && <div />}

                      {!disableWS && groupNo === lastGroupIndex && (
                        <TotalSurchargeParam
                          className={"pl-2"}
                          product={product}
                          entry={entry}
                          isInCheckout={isInCheckout}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </Fragment>
          ))}

          {!isPnaLoading && isPnaError && !isConfiguratorProduct(product) && !disableWS && (
            <div className="px-3 my-3">
              <Alert severity={"error"} className={"my-2"}>
                {t("page.cart.product.pnaLoadingError")}
              </Alert>
            </div>
          )}
          {showSubtotal && !isConfiguratorProduct(product) && (
            <PlainSubtotalsRow product={product} entry={entry} isInCheckout={isInCheckout} />
          )}
        </div>
      </div>
    </SalesDataProvider>
  );
};

interface PlainSubtotalProps {
  className?: string;
  entry: CartEntry;
  product: ProductData;
  isInCheckout: boolean;
}

const TotalSurchargeParam = ({ entry, product, className }: PlainSubtotalProps) => {
  const hasSurcharge = useSelector((state: RootState) => selectCartEntryHasSurcharge(state, { cartEntryId: entry.id }));
  const totalSurcharge = useSelector((state: RootState) =>
    selectTotalSurcharge(state, { cartEntryId: entry.id, quantityVisible: product.quantityVisible }),
  );

  if (!hasSurcharge) {
    return null;
  }

  return <IncludedSurcharge value={totalSurcharge} className={className} />;
};

const PlainSubtotalsRow = ({ entry, product, isInCheckout }: PlainSubtotalProps) => {
  const subtotal = useSelector((state: RootState) =>
    selectSubtotal(state, {
      productCode: product.code,
      isInCheckout,
    }),
  );
  const totalQty = useSelector((state: RootState) => selectTotalQty(state, { productCode: product.code }));
  const hasSurcharge = useSelector((state: RootState) => selectCartEntryHasSurcharge(state, { cartEntryId: entry.id }));

  const deliveryAmount = useSelector((state: RootState) => {
    return entry.packEntryIds
      .map((packEntryId) => {
        const pack = selectPackageEntry(packEntryId)(state);
        if (!pack.partialDeliveries) {
          return 1;
        }
        return getPhysicalDeliveries(pack.partialDeliveries).length || 1;
      })
      .reduce((acc, val) => acc + val, 0);
  });

  return (
    <SubtotalsRow
      subtotal={subtotal}
      totalQty={totalQty}
      deliveryAmount={deliveryAmount}
      product={product}
      hasSurcharge={hasSurcharge}
    />
  );
};

interface SubtotalProps {
  subtotal: PriceData;
  totalQty: number;
  deliveryAmount: number;
  product: ProductData;
  hasSurcharge?: boolean;
}

export const SubtotalsRow = ({ subtotal, totalQty, deliveryAmount, product, hasSurcharge }: SubtotalProps) => {
  return (
    <div className="scha__cart__product-table__right-side__summary-container mt-30">
      <QuantitySubtotal className={"pl-3 pr-2"} quantity={totalQty} />
      <DeliverySubtotal className={"pl-2 pr-2"} quantity={deliveryAmount} isSubscription={!product.quantityVisible} />
      <PriceSubtotal className={"pl-2 pr-3"} price={subtotal} hasSurcharge={hasSurcharge} />
    </div>
  );
};

export const CopyLastRowLink = ({ entryId }: { entryId: number }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    dispatch(copyLastPackageEntry(entryId));
  };

  const lineItem = useSelector(selectLineItem(entryId));
  const isExceedingSalesQuantity = !!lineItem.messages.find((msg) => msg.key === CART_QTY_EXCEEDS_SALES_ERROR_KEY);

  return (
    <Link
      data-test-id={"btn-add-row"}
      onClick={onClick}
      iconBefore={<Icon type={IconType.Plus} />}
      disabled={isExceedingSalesQuantity}
    >
      {t("cartPage.table.copyRow")}
    </Link>
  );
};

export const LoadPnaLink = ({
  isEditMode,
  productCode,
  disabled,
  loadAvailabilityOnly,
}: {
  isEditMode?: boolean;
  productCode: string;
  disabled: boolean;
  loadAvailabilityOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loadPrice = isEditMode && !loadAvailabilityOnly;

  const onClick: React.MouseEventHandler = (e) => {
    e.preventDefault();

    if (loadPrice) {
      dispatch(loadPnACartEntry(productCode));
    } else {
      dispatch(loadACartEntry(productCode));
    }
  };

  const labelKey = `page.cart.table.${loadPrice ? "loadPna" : "loadA"}`;

  return (
    <LinkButton
      iconBefore={<Icon type={IconType.Reload} />}
      data-test-id={"btn-load-pna"}
      variant={LinkType.PRIMARY}
      onClick={onClick}
      disabled={disabled}
    >
      {t(labelKey)}
    </LinkButton>
  );
};

export default memo(PlainProductEntry);

const getUniquePackageCodes = (packageEntries: LineItem[]) => {
  return uniq(packageEntries.map(($) => $.packCode));
};
